import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { hubProjectKey } from '../../../domain/infrastructure';
import { LOUNGE_TIERS } from '../../../domain/infrastructure/types';
import type { BaseTier, HubAirportView, HubProjectQuote, LoungeTier } from '../../../domain/infrastructure/types';
import type { MarketReachView } from '../../../domain/demand/marketViews';
import type {
  AirportView,
  FleetAircraftView,
  RouteAnalyticsView,
  RouteSeatShareView,
  UICommand,
} from '../../../domain/models/contracts';
import { MarketReachPanel } from '../analytics/MarketReachPanel';
import { HubOverview } from './HubOverview';
import { RouteDetailModal } from '../analytics/RouteDetailModal';
import { routesTouchingAirport } from '../analytics/routeAggregates';
import { formatInt, formatUsd } from '../analytics/formatters';
import './HubManager.css';

export interface HubManagerProps {
  readonly hubs: readonly HubAirportView[];
  readonly airportsById: Readonly<Record<string, AirportView>>;
  /** Cash on hand, or null before the first network state. */
  readonly cashUSD: number | null;
  readonly onCommand: (command: UICommand) => void;
  /** Per-route analytics; the routes touching each hub open in the shared route detail modal. */
  readonly analytics: readonly RouteAnalyticsView[];
  readonly competitorShares: readonly RouteSeatShareView[];
  readonly fleet: readonly FleetAircraftView[];
  /** Display name per aircraft type id, for the aircraft mix. */
  readonly typeNames: Readonly<Record<string, string>>;
  /** Latest catchment answer from the worker; shown only when it matches the selected hub. */
  readonly marketReach: MarketReachView | null;
  /** Asks the worker for the catchment around a hub. */
  readonly onRequestMarketReach: (airportId: string) => void;
}

const TIER_LABELS: Readonly<Record<BaseTier, string>> = {
  SPOKE_STATION: 'Spoke',
  FOCUS_CITY: 'Focus city',
  SECONDARY_HUB: 'Secondary hub',
  PRIMARY_GLOBAL_HUB: 'Primary global hub',
};

const LOUNGE_LABELS: Readonly<Record<LoungeTier, string>> = {
  THIRD_PARTY_CONTRACT: 'Contract',
  DEDICATED_BUSINESS_LOUNGE: 'Business',
  FLAGSHIP_FIRST_LOUNGE: 'Flagship First',
};

/**
 * Hubs and infrastructure: one card per airport the airline serves or has built
 * at. Tier progress, running costs and MRO income come from the engine's hub
 * cards; every upgrade button sends UPGRADE_AIRPORT_INFRASTRUCTURE with the
 * project the engine priced.
 */
export function HubManager({
  hubs,
  airportsById,
  cashUSD,
  onCommand,
  analytics,
  competitorShares,
  fleet,
  typeNames,
  marketReach,
  onRequestMarketReach,
}: HubManagerProps): ReactElement {
  const [openAirportId, setOpenAirportId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectHub = (airportId: string): void => {
    setSelectedId(airportId);
    onRequestMarketReach(airportId);
  };
  const selectedReach = marketReach !== null && marketReach.airportId === selectedId ? marketReach : null;
  const totals = useMemo(
    () =>
      hubs.reduce(
        (sum, hub) => ({
          operatingUSD: sum.operatingUSD + hub.dailyOperatingCostUSD,
          mroUSD: sum.mroUSD + hub.dailyMroLeaseRevenueUSD,
          builtSites: sum.builtSites + (hasInvestment(hub) ? 1 : 0),
        }),
        { operatingUSD: 0, mroUSD: 0, builtSites: 0 },
      ),
    [hubs],
  );

  if (hubs.length === 0) {
    return (
      <div className="hub-empty">
        <p>No hubs yet. Airports appear here once the network flies from or to them.</p>
      </div>
    );
  }

  return (
    <div className="hub-manager">
      <dl className="hub-summary">
        <div>
          <dt>Airports</dt>
          <dd>{formatInt(hubs.length)}</dd>
        </div>
        <div>
          <dt>Sites with investment</dt>
          <dd>{formatInt(totals.builtSites)}</dd>
        </div>
        <div>
          <dt>Infrastructure running cost</dt>
          <dd>{formatUsd(totals.operatingUSD)} / day</dd>
        </div>
        <div>
          <dt>MRO lease income</dt>
          <dd className={totals.mroUSD > 0 ? 'pos' : undefined}>{formatUsd(totals.mroUSD)} / day</dd>
        </div>
      </dl>

      <section className="hub-inspector" aria-label="Hub inspector">
        {selectedId === null ? (
          <p className="sub">Select a hub card to see its traffic and the population it draws on.</p>
        ) : (
          <>
            <HubOverview
              airportId={selectedId}
              airport={airportsById[selectedId]}
              tierLabel={TIER_LABELS[hubs.find((h) => h.airportId === selectedId)?.baseTier ?? 'SPOKE_STATION']}
              weeklyDepartures={hubs.find((h) => h.airportId === selectedId)?.weeklyDepartures ?? 0}
              routes={routesTouchingAirport(analytics, selectedId)}
              airportsById={airportsById}
              competitorShares={competitorShares}
              fleet={fleet}
              typeNames={typeNames}
              reach={selectedReach}
            />
            <MarketReachPanel reach={selectedReach} />
          </>
        )}
      </section>

      <div className="hub-grid">
        {hubs.map((hub) => (
          <HubCard
            key={hub.airportId}
            hub={hub}
            airport={airportsById[hub.airportId]}
            cashUSD={cashUSD}
            selected={hub.airportId === selectedId}
            onSelect={() => selectHub(hub.airportId)}
            routeCount={routesTouchingAirport(analytics, hub.airportId).length}
            onOpenRoutes={() => setOpenAirportId(hub.airportId)}
            onBuild={(project) => onCommand({ type: 'UPGRADE_AIRPORT_INFRASTRUCTURE', airportId: hub.airportId, project })}
          />
        ))}
      </div>

      {openAirportId !== null && (
        <RouteDetailModal
          title={`${openAirportId} hub routes`}
          subtitle={`${formatInt(routesTouchingAirport(analytics, openAirportId).length)} published routes touching this airport`}
          routes={routesTouchingAirport(analytics, openAirportId)}
          competitorShares={competitorShares}
          onClose={() => setOpenAirportId(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

/** True once the airport has anything beyond the default spoke. */
function hasInvestment(hub: HubAirportView): boolean {
  const infra = hub.infrastructure;
  return (
    hub.baseTier !== 'SPOKE_STATION' ||
    infra.lineMaintenance ||
    infra.heavyHangar ||
    infra.ownedGroundHandling ||
    infra.inHouseCatering ||
    infra.loungeTier !== null ||
    infra.leasedMroBays > 0
  );
}

interface HubCardProps {
  readonly hub: HubAirportView;
  readonly airport: AirportView | undefined;
  readonly cashUSD: number | null;
  readonly selected: boolean;
  readonly onSelect: () => void;
  /** Published routes that start or end at this airport. */
  readonly routeCount: number;
  readonly onOpenRoutes: () => void;
  readonly onBuild: (project: HubProjectQuote['project']) => void;
}

function HubCard({ hub, airport, cashUSD, selected, onSelect, routeCount, onOpenRoutes, onBuild }: HubCardProps): ReactElement {
  const find = (key: string): HubProjectQuote | undefined => hub.projects.find((p) => p.key === key);
  const infra = hub.infrastructure;
  const baseProject = hub.nextTier !== null ? find(hubProjectKey({ kind: 'BASE_TIER', tier: hub.nextTier.tier })) : undefined;
  const mroProject = hub.projects.find((p) => p.project.kind === 'MRO_LEASING');
  const loungeProjects = LOUNGE_TIERS.map((tier) => find(hubProjectKey({ kind: 'LOUNGE', tier })));

  return (
    <article
      className={`hub-card${selected ? ' is-selected' : ''}`}
      aria-label={`${hub.airportId} hub`}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <header className="hub-card-head">
        <div>
          <h3 className="hub-code">{hub.airportId}</h3>
          <p className="hub-place sub">{airport ? `${airport.name}, ${airport.country}` : 'Unknown airport'}</p>
        </div>
        <span className={`hub-tier hub-tier-${hub.baseTier.toLowerCase()}`}>{TIER_LABELS[hub.baseTier]}</span>
      </header>

      <dl className="hub-stats">
        <div>
          <dt>Weekly departures</dt>
          <dd>{formatInt(hub.weeklyDepartures)}</dd>
        </div>
        <div>
          <dt>Peak slots held</dt>
          <dd>{formatInt(hub.peakSlotsHeld)}</dd>
        </div>
        <div>
          <dt>Competitors</dt>
          <dd>{formatInt(hub.competingCarriers)}</dd>
        </div>
      </dl>

      <section className="hub-section" aria-label="Base tier progress">
        <h4 className="hub-section-title">Base tier</h4>
        {hub.nextTier === null ? (
          <p className="sub">Top tier reached.</p>
        ) : (
          <>
            <ProgressBar
              label="Weekly departures"
              value={hub.nextTier.weeklyDepartures}
              target={hub.nextTier.minWeeklyDepartures}
            />
            <ProgressBar label="Peak-hour slots" value={hub.nextTier.peakSlotsHeld} target={hub.nextTier.minPeakSlotsHeld} />
            <p className="sub">
              Next: {TIER_LABELS[hub.nextTier.tier]} for {formatUsd(hub.nextTier.stepUpCapexUSD)}
              {hub.nextTier.minPeakSlotsHeld > 0 && ' and slot licences'}.
            </p>
            {baseProject !== undefined && (
              <ProjectButton project={baseProject} cashUSD={cashUSD} onBuild={onBuild} />
            )}
          </>
        )}
      </section>

      <section className="hub-section" aria-label="Maintenance and handling">
        <h4 className="hub-section-title">Maintenance and handling</h4>
        <ul className="hub-projects">
          <BuiltOrProject
            label="Line maintenance"
            built={infra.lineMaintenance}
            project={find('LINE_MAINTENANCE')}
            cashUSD={cashUSD}
            onBuild={onBuild}
          />
          <BuiltOrProject
            label="Heavy hangar (C and D checks)"
            built={infra.heavyHangar}
            project={find('HEAVY_HANGAR')}
            cashUSD={cashUSD}
            onBuild={onBuild}
          />
          <BuiltOrProject
            label="Owned ground handling"
            built={infra.ownedGroundHandling}
            project={find('OWNED_GROUND_HANDLING')}
            cashUSD={cashUSD}
            onBuild={onBuild}
          />
          <BuiltOrProject
            label="In-house catering"
            built={infra.inHouseCatering}
            project={find('IN_HOUSE_CATERING')}
            cashUSD={cashUSD}
            onBuild={onBuild}
          />
        </ul>
      </section>

      <section className="hub-section" aria-label="MRO leasing">
        <h4 className="hub-section-title">MRO hangar leasing</h4>
        <p className="sub">
          {infra.heavyHangar
            ? `${infra.leasedMroBays} of 2 leasable bays let to competitors.`
            : 'Build a heavy hangar to lease bays to competitor overhauls.'}
        </p>
        {mroProject !== undefined && infra.heavyHangar && <ProjectButton project={mroProject} cashUSD={cashUSD} onBuild={onBuild} />}
        <p className="hub-income">
          <span className="sub">Lease income</span>
          <span className={hub.dailyMroLeaseRevenueUSD > 0 ? 'pos' : 'muted'}>{formatUsd(hub.dailyMroLeaseRevenueUSD)} / day</span>
        </p>
      </section>

      <section className="hub-section" aria-label="Lounges">
        <h4 className="hub-section-title">Lounge</h4>
        <ul className="hub-projects">
          {LOUNGE_TIERS.map((tier, index) => {
            const project = loungeProjects[index];
            const current = infra.loungeTier === tier;
            return (
              <BuiltOrProject
                key={tier}
                label={LOUNGE_LABELS[tier]}
                built={current}
                project={project}
                cashUSD={cashUSD}
                onBuild={onBuild}
              />
            );
          })}
        </ul>
      </section>

      <footer className="hub-card-foot">
        <span className="sub">Running cost</span>
        <span>{formatUsd(hub.dailyOperatingCostUSD)} / day</span>
        <button type="button" className="hub-routes-btn" onClick={onOpenRoutes} disabled={routeCount === 0}>
          Routes &amp; analytics ({formatInt(routeCount)})
        </button>
      </footer>
    </article>
  );
}

/* ------------------------------------------------------------------------ */

interface ProgressBarProps {
  readonly label: string;
  readonly value: number;
  readonly target: number;
}

function ProgressBar({ label, value, target }: ProgressBarProps): ReactElement {
  const pct = target <= 0 ? 100 : Math.min(100, (value / target) * 100);
  const met = value >= target;
  return (
    <div className="hub-progress">
      <div className="hub-progress-label">
        <span className="sub">{label}</span>
        <span className={met ? 'pos' : undefined}>
          {formatInt(value)} / {formatInt(target)}
        </span>
      </div>
      <div
        className="hub-progress-track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={target}
        aria-valuenow={Math.min(value, target)}
      >
        <div className={`hub-progress-fill${met ? ' is-met' : ''}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

interface BuiltOrProjectProps {
  readonly label: string;
  readonly built: boolean;
  readonly project: HubProjectQuote | undefined;
  readonly cashUSD: number | null;
  readonly onBuild: (project: HubProjectQuote['project']) => void;
}

function BuiltOrProject({ label, built, project, cashUSD, onBuild }: BuiltOrProjectProps): ReactElement {
  return (
    <li className="hub-project">
      <span className="hub-project-label">
        {label}
        {built && <span className="hub-built">In place</span>}
      </span>
      {!built && project !== undefined && <ProjectButton project={project} cashUSD={cashUSD} onBuild={onBuild} />}
    </li>
  );
}

interface ProjectButtonProps {
  readonly project: HubProjectQuote;
  readonly cashUSD: number | null;
  readonly onBuild: (project: HubProjectQuote['project']) => void;
}

function ProjectButton({ project, cashUSD, onBuild }: ProjectButtonProps): ReactElement {
  const { quote } = project;
  if (!quote.ok) {
    const reason = quote.reasons[0] ?? 'Not available';
    return (
      <span className="hub-blocked sub" title={quote.reasons.join('\n')}>
        {reason}
      </span>
    );
  }
  const cannotAfford = cashUSD !== null && quote.capexUSD > cashUSD;
  return (
    <button
      type="button"
      className="btn btn-small btn-primary"
      disabled={cannotAfford}
      onClick={() => onBuild(project.project)}
      title={cannotAfford ? 'Not enough cash for this project' : `${project.label} for ${formatUsd(quote.capexUSD)}`}
    >
      {project.label}: {formatUsd(quote.capexUSD)}
    </button>
  );
}
